import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { DiscountInput } from "@reina/shared";
import type { Brand, Discount, Product, RateCard, Supplier } from "@reina/shared";
import {
  Button,
  EmptyState,
  Field,
  FieldGrid,
  Modal,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  SupplierAvatar,
  TableWrap,
  useToast,
} from "../../components";
import {
  useBrands,
  useCreateDiscount,
  useDiscounts,
  useRateCards,
  useSuppliers,
  useUpdateDiscount,
} from "../../api/catalogue";
import { useProducts } from "../../api/products";
import { errorMessage, formatDate, formatPct } from "../../lib/format";
import { numberOrUndefined, textOrNull } from "../../lib/form";

export function DiscountsPage() {
  const [supplierFilter, setSupplierFilter] = useState("");
  const suppliers = useSuppliers();
  const brands = useBrands();
  const rateCards = useRateCards();
  const products = useProducts();
  const discounts = useDiscounts(
    supplierFilter ? { limit: 500, supplierId: supplierFilter } : { limit: 500 }
  );
  const items = discounts.data?.items ?? [];

  const supplierById = useMemo(
    () => new Map((suppliers.data?.items ?? []).map((s) => [s.id, s])),
    [suppliers.data]
  );
  const brandById = useMemo(
    () => new Map((brands.data?.items ?? []).map((b) => [b.id, b])),
    [brands.data]
  );
  const rateCardById = useMemo(
    () => new Map((rateCards.data?.items ?? []).map((r) => [r.id, r])),
    [rateCards.data]
  );
  const productById = useMemo(
    () => new Map((products.data?.items ?? []).map((p) => [p.id, p])),
    [products.data]
  );

  const [modal, setModal] = useState<{ discount: Discount | null } | null>(null);

  const newButton = (
    <Button variant="primary" onClick={() => setModal({ discount: null })}>
      New discount
    </Button>
  );

  return (
    <>
      <Panel>
        <PanelHead
          title="Discounts"
          sub={
            discounts.data
              ? `${discounts.data.total} standing rule${discounts.data.total === 1 ? "" : "s"}`
              : "supplier discounts, optionally scoped"
          }
          actions={
            <>
              <select
                className="field"
                style={{ width: 220 }}
                value={supplierFilter}
                onChange={(event) => setSupplierFilter(event.target.value)}
                aria-label="Filter by supplier"
              >
                <option value="">All suppliers</option>
                {(suppliers.data?.items ?? []).map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
              {newButton}
            </>
          }
        />
        {discounts.isPending ? (
          <PanelBody>
            <EmptyState title="Loading discounts…" />
          </PanelBody>
        ) : discounts.isError ? (
          <PanelBody>
            <EmptyState title="Could not load discounts" hint={errorMessage(discounts.error)} />
          </PanelBody>
        ) : items.length === 0 ? (
          <PanelBody>
            <EmptyState
              title="No discounts"
              hint="Standing supplier discounts, scopeable to a brand, product or rate card."
              action={newButton}
            />
          </PanelBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Scope</th>
                <th>Discount</th>
                <th>Valid from</th>
                <th>Valid to</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((discount) => {
                const supplier = supplierById.get(discount.supplierId);
                const brand = discount.brandId ? brandById.get(discount.brandId) : undefined;
                const product = discount.productId ? productById.get(discount.productId) : undefined;
                const rateCard = discount.rateCardId
                  ? rateCardById.get(discount.rateCardId)
                  : undefined;
                const scoped = discount.brandId || discount.productId || discount.rateCardId;
                return (
                  <tr
                    key={discount.id}
                    onClick={() => setModal({ discount })}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      {supplier ? (
                        <span className="result-cell">
                          <SupplierAvatar name={supplier.name} logo={supplier.logo} />
                          <strong>{supplier.name}</strong>
                        </span>
                      ) : (
                        <span className="mono">{discount.supplierId.slice(0, 8)}</span>
                      )}
                    </td>
                    <td>
                      {scoped ? (
                        <span className="actions" style={{ justifyContent: "flex-start" }}>
                          {discount.brandId && (
                            <Pill tone="neutral">{brand?.name ?? "Brand"}</Pill>
                          )}
                          {discount.productId && (
                            <Pill tone="neutral" title={product?.description}>
                              {product
                                ? product.iqCode ?? product.description.slice(0, 24)
                                : "Product"}
                            </Pill>
                          )}
                          {discount.rateCardId && (
                            <Pill tone="neutral">{rateCard?.termsCode ?? "Rate card"}</Pill>
                          )}
                        </span>
                      ) : (
                        "All items"
                      )}
                    </td>
                    <td className="money">{formatPct(discount.discountPct)}</td>
                    <td className="mono">{formatDate(discount.validFrom)}</td>
                    <td className="mono">{formatDate(discount.validTo)}</td>
                    <td>
                      <Button
                        mini
                        onClick={(event) => {
                          event.stopPropagation();
                          setModal({ discount });
                        }}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </Panel>
      {modal && (
        <DiscountModal
          key={modal.discount?.id ?? "new"}
          discount={modal.discount}
          suppliers={suppliers.data?.items ?? []}
          brands={brands.data?.items ?? []}
          rateCards={rateCards.data?.items ?? []}
          products={products.data?.items ?? []}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Create/edit modal
// ---------------------------------------------------------------------------

function DiscountModal({
  discount,
  suppliers,
  brands,
  rateCards,
  products,
  onClose,
}: {
  discount: Discount | null;
  suppliers: Supplier[];
  brands: Brand[];
  rateCards: RateCard[];
  products: Product[];
  onClose: () => void;
}) {
  const toast = useToast();
  const create = useCreateDiscount();
  const update = useUpdateDiscount();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof DiscountInput>, unknown, z.output<typeof DiscountInput>>({
    resolver: zodResolver(DiscountInput),
    defaultValues: {
      supplierId: discount?.supplierId ?? "",
      brandId: discount?.brandId ?? "",
      productId: discount?.productId ?? "",
      rateCardId: discount?.rateCardId ?? "",
      discountPct: discount?.discountPct ?? undefined,
      validFrom: discount?.validFrom ?? "",
      validTo: discount?.validTo ?? "",
    },
  });

  const chosenSupplierId = watch("supplierId");
  const supplierRateCards = rateCards.filter(
    (rateCard) => !chosenSupplierId || rateCard.supplierId === chosenSupplierId
  );

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (discount) {
        await update.mutateAsync({ id: discount.id, input: values });
        toast.push({ message: "Discount updated", tone: "ok" });
      } else {
        await create.mutateAsync(values);
        toast.push({ message: "Discount created", tone: "ok" });
      }
      onClose();
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  });

  return (
    <Modal
      open
      title={discount ? "Edit discount" : "New discount"}
      sub="leave brand/product/rate card empty for a supplier-wide rule"
      onClose={onClose}
      footer={
        <>
          <span className="sub">Most specific matching rule wins at pricing time.</span>
          <div className="actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="discount-form" disabled={isSubmitting}>
              {discount ? "Save changes" : "Create discount"}
            </Button>
          </div>
        </>
      }
    >
      <form id="discount-form" className="stack" onSubmit={onSubmit} noValidate>
        <FieldGrid>
          <Field label="Supplier" error={errors.supplierId ? "Choose a supplier" : undefined}>
            <select className="field" {...register("supplierId")}>
              <option value="">Choose…</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Discount %" error={errors.discountPct?.message}>
            <input
              className="field"
              type="number"
              step="0.01"
              min={0}
              max={100}
              {...register("discountPct", { setValueAs: numberOrUndefined })}
            />
          </Field>
          <Field label="Brand (optional)" error={errors.brandId ? "Invalid brand" : undefined}>
            <select className="field" {...register("brandId", { setValueAs: textOrNull })}>
              <option value="">Any brand</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Rate card (optional)"
            error={errors.rateCardId ? "Invalid rate card" : undefined}
          >
            <select className="field" {...register("rateCardId", { setValueAs: textOrNull })}>
              <option value="">Any rate card</option>
              {supplierRateCards.map((rateCard) => (
                <option key={rateCard.id} value={rateCard.id}>
                  {rateCard.termsCode} — {rateCard.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Product (optional)"
            hint={products.length === 0 ? "Products appear after the first price-list ingest." : undefined}
            error={errors.productId ? "Invalid product" : undefined}
          >
            <select className="field" {...register("productId", { setValueAs: textOrNull })}>
              <option value="">Any product</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.iqCode ? `${product.iqCode} — ` : ""}
                  {product.description}
                </option>
              ))}
            </select>
          </Field>
        </FieldGrid>
        <FieldGrid>
          <Field label="Valid from (optional)" error={errors.validFrom?.message}>
            <input className="field" type="date" {...register("validFrom", { setValueAs: textOrNull })} />
          </Field>
          <Field label="Valid to (optional)" error={errors.validTo?.message}>
            <input className="field" type="date" {...register("validTo", { setValueAs: textOrNull })} />
          </Field>
        </FieldGrid>
      </form>
    </Modal>
  );
}
